import axios from "axios";
import { API_BASE_URL } from "../config"; // Импортируем базовый URL из config.js
import { clearAccessToken, decodeAccessToken, getAccessToken, logoutSession } from "./authSession";

const API_URL = `${API_BASE_URL}/developer/settings`;
const DATA_FORMATS_URL = `${API_BASE_URL}/data_formats/`;

const clearStoredAuth = () => {
    clearAccessToken();
};

const redirectToLogin = () => {
    clearStoredAuth();
    window.location.href = "/";
};

const getStoredLogin = () => {
    // The login is intentionally derived only from the in-memory access JWT.
    return decodeAccessToken(getAccessToken() || "")?.sub || null;
};

const handleAuthError = (error) => {
    const status = error?.response?.status;
    // Только 401 = "не авторизован" (нет/протух токен) → на экран входа.
    // 403 = "нет прав" при валидном токене — не разлогиниваем, пусть страница
    // покажет ошибку (иначе 403 на любом справочнике выкидывает на логин).
    if (status === 401) {
        redirectToLogin();
    }
};

// 🔹 Функция для получения токена
const getAuthToken = () => {
    const token = getAccessToken();
    if (!token) {
        console.error("Ошибка: Токен отсутствует");
        redirectToLogin();
        return null;
    }
    return token;
};

// 🔹 Конфиг для axios с заголовком Authorization
const getAuthHeaders = () => {
    const token = getAuthToken();
    return token
        ? {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        }
        : {};
};

export { getAuthHeaders, handleAuthError, redirectToLogin, getAuthToken };

// 🔹 Получение настроек разработчика по логину
const getSetting = async () => {
    const login = getStoredLogin();
    if (!login) {
        console.error("Ошибка: Логин пользователя не найден в localStorage");
        redirectToLogin();
        return null;
    }

    try {
        console.log(`📌 Запрашиваем настройки для: ${login}`);
        const response = await axios.get(`${API_URL}/${login}`, getAuthHeaders());
        console.log("✅ Полученные настройки:", response.data);
        return response.data;
    } catch (error) {
        handleAuthError(error);
        console.error("❌ Ошибка получения настроек:", error);
        throw error;
    }
};

// 🔹 Обновление настроек разработчика
const updateSetting = async (data) => {
    const login = getStoredLogin();
    if (!login) {
        console.error("Ошибка: Логин отсутствует");
        redirectToLogin();
        return;
    }

    if (!data || Object.keys(data).length === 0) {
        console.error("🚨 Ошибка: Передаваемые данные пустые!", data);
        return;
    }

    console.log("📡 Отправка данных на сервер:", JSON.stringify(data, null, 2));

    try {
        const response = await axios.put(`${API_URL}/${login}`, data, getAuthHeaders());
        console.log("✅ Ответ сервера:", response.data);
        return response.data;
    } catch (error) {
        handleAuthError(error);
        console.error(`❌ Ошибка при обновлении настроек для ${login}:`, error);
        throw error;
    }
};

// Обновляет только поля текущего UI-блока, сохраняя остальные настройки актуальными.
const updateSettingFields = async (fields) => {
    const current = await getSetting();
    return updateSetting({
        ...current,
        ...fields,
    });
};

// 🔹 Получение списка всех форматов данных
const getDataFormats = async () => {
    try {
        console.log("📌 Запрашиваем форматы данных...");
        const response = await axios.get(DATA_FORMATS_URL, getAuthHeaders());
        console.log("✅ Получены форматы данных:", response.data);
        return response.data;
    } catch (error) {
        handleAuthError(error);
        console.error("❌ Ошибка загрузки форматов данных:", error);
        throw error;
    }
};

// 🔹 Добавление нового формата данных
const addDataFormat = async (newFormat) => {
    const data = typeof newFormat === "string" ? { format_name: newFormat } : newFormat;
    try {
        console.log("📌 Добавляем новый формат:", JSON.stringify(data, null, 2));
        const response = await axios.post(DATA_FORMATS_URL, data, getAuthHeaders());
        console.log("✅ Формат добавлен:", response.data);
        return response.data;
    } catch (error) {
        handleAuthError(error);
        console.error("❌ Ошибка добавления формата данных:", error);
        throw error;
    }
};

// 🔹 Выход из системы (удаление токена и логина)
const logout = async () => {
    console.warn("🚪 Выход из системы...");
    await logoutSession();
    redirectToLogin();
};

// 🔹 Экспортируем API-функции
const developerApi = {
    getSetting,
    updateSetting,
    updateSettingFields,
    getDataFormats,
    addDataFormat,
    logout
};

export default developerApi;
